import { rms } from './utilities.js'

/**
 * Where a note actually started, to the sample.
 *
 * A note's onset is timestamped to its first voiced analysis frame, and frames arrive one per
 * 1024-sample hop - 21.3 ms at 48 kHz. Which side of a hop a note lands on is decided by the phase
 * of the performance against the analysis clock and by nothing the musician did, so the onset error
 * is uniform on ±10.7 ms before any music has happened. The Melody Trainer's tightest timing band is
 * 25 ms (§6.2), so a note near a band edge changes verdict on hop phase alone. That is the floor
 * under every timing judgement the tool gives.
 *
 * The worklet holds the raw samples, so the fix belongs on the audio thread. The detector and the
 * hop are unchanged: halving the hop would double the detector's load for every pitch surface in the
 * app, while this costs one short scan per note onset and nothing at all in between.
 *
 * @see docs/superpowers/specs/2026-09-08-melody-plugin-design.md §8
 */

/**
 * The foot of the attack is the last point whose short-window RMS is still under this fraction of
 * the frame's own RMS.
 *
 * A fraction of the FRAME, not an absolute level. The frame that first clears voicing is typically
 * part silence and part note, so its RMS sits well below the note's steady amplitude and 0.15 of it
 * lands at a few per cent of the note - low on the attack ramp, which is where a foot is. An
 * absolute threshold would find the foot of a loud note and the middle of a quiet one, and would
 * make the onset depend on the microphone gain.
 */
export const onsetRmsFraction = 0.15

/**
 * The short window the RMS is measured over. 128 samples is 2.7 ms at 48 kHz - short enough to
 * resolve an attack, long enough that one period of a low note does not read as silence.
 */
export const onsetRmsWindow = 128

/**
 * The step between measurements. 32 samples puts the answer on a 0.67 ms grid, well inside the 3 ms
 * this refinement is held to, and cheap: 60 windows per onset, and only on an onset.
 */
export const onsetRmsHop = 32

/**
 * The worklet's own voicing proxy: a frame counts as voiced at this clarity or above.
 *
 * Deliberately a local copy of `VocalLib.voicingClarityMin` rather than an import. This module is
 * bundled by tsup into the worklet, and importing the scoring library would put it on the audio
 * thread. The full gate downstream is clarity AND level-above-the-measured-noise-floor, and the
 * floor is the median of the head of the whole capture - which a worklet running frame by frame
 * cannot know.
 *
 * Clarity alone is the LOOSER of the two conditions, so this flags the frame the full gate keeps or
 * an earlier one, and never a later one. A frame the full gate then drops simply carries an offset
 * nobody reads. That asymmetry is what makes the proxy safe; `onset-refine.test.ts` asserts the two
 * values are still equal.
 */
export const onsetVoicingClarityMin = 0.9

/**
 * The foot of the attack: the last point in `scan` at which the short-window RMS is still below the
 * threshold, before it rises past it. Not the point where the note crosses the voicing gate.
 *
 * Searched from the END backwards, which is what "last" buys. When a re-articulated note leaves the
 * tail of the previous note inside the scan, the earlier crossing belongs to that note and the later
 * one to the note being timestamped; searching forwards would report the wrong note's onset.
 *
 * **Walking further back down the rise was tried and is worse.** Continuing from the crossing while
 * each earlier window is quieter looks like it should find a truer foot, and on the §8 fixture it
 * does the opposite: it walks into the room tone, whose window-to-window RMS wanders, and lands
 * 2.7-4.7 ms EARLY on every note (measured 2026-09-10, against 1.33 ms worst for the rule below).
 * Requiring a minimum decay per step to stop it stalls immediately on a slow ramp instead, so it
 * buys the early bias and fixes nothing. The threshold crossing is where the evidence is.
 *
 * Returns `null` when the RMS never falls below the threshold: the attack began before the scan
 * region and there is no foot in view. `null` is the honest answer and it is what keeps this
 * refinement additive - the caller falls back to the frame timestamp, which is exactly what the
 * chain did before this existed.
 *
 * The index returned is the START of the window measured, so the answer is biased one short window
 * early rather than late. That is the right direction for an onset: the foot of an attack is before
 * the energy, not after it.
 */
export const findAttackFoot = (scan: Float32Array, frameRms: number): number | null => {
  const threshold = onsetRmsFraction * frameRms
  /*
   * A silent frame has a zero threshold, under which every window would qualify - so silence would
   * report an onset at the end of the scan. `Number.isFinite` covers the NaN a corrupt frame gives.
   */
  if (!Number.isFinite(threshold) || threshold <= 0) {
    return null
  }

  for (let start = scan.length - onsetRmsWindow; start >= 0; start -= onsetRmsHop) {
    if (rms(scan.subarray(start, start + onsetRmsWindow)) < threshold) {
      return start
    }
  }
  return null
}
